import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { stripeService } from '../../services/stripe';
import { supabase } from '../../lib/supabase';
import { PLAN_SLUGS, getPlanConfig } from '../../config/plans';

const Settings = () => {
  const navigate = useNavigate();
  const { userProfile, updateProfile, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [hasFirstYearDiscount, setHasFirstYearDiscount] = useState(false);
  
  const [defaultValues, setDefaultValues] = useState({
    defaultInterestRate: '',
    defaultAmortizationPeriod: '25',
    defaultDownPaymentPercent: '20',
    defaultRentGrowthRate: '2',
    defaultAppreciationRate: '3',
    defaultExpenseGrowthRate: '2',
    defaultVacancyRate: '5',
    defaultCollectionRate: '95',
    defaultManagementFeePercent: '8'
  });

  useEffect(() => {
    // Load saved default values from user profile or localStorage
    const savedDefaults = localStorage.getItem('propertyDefaults');
    if (savedDefaults) {
      try {
        const parsed = JSON.parse(savedDefaults);
        setDefaultValues(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        console.error('Error loading defaults:', e);
      }
    }
  }, []);

  // Load subscription data
  useEffect(() => {
    const loadSubscription = async () => {
      if (!userProfile?.id) return;
      
      try {
        const { data } = await stripeService?.getUserSubscription(userProfile.id);
        if (data) {
          setSubscription(data);
          
          // Check if user has had annual discount before
          const { data: subscriptions } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', userProfile.id)
            .order('created_at', { ascending: false });
          
          const hasHadAnnualDiscount = subscriptions?.some(sub => {
            // Check if subscription was annual with discount
            return sub.tier && [PLAN_SLUGS.ANALYSIS, PLAN_SLUGS.PRO, 'plus'].includes(sub.tier);
          });
          
          setHasFirstYearDiscount(hasHadAnnualDiscount || subscriptions?.length === 0);
        }
      } catch (error) {
        console.error('Error loading subscription:', error);
      }
    };

    loadSubscription();
  }, [userProfile?.id]);

  const handleChange = (field, value) => {
    setDefaultValues(prev => ({
      ...prev,
      [field]: value
    }));
    setSaved(false);
  };

  const handleSave = () => {
    setLoading(true);
    try {
      // Save to localStorage
      localStorage.setItem('propertyDefaults', JSON.stringify(defaultValues));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving defaults:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser toutes les valeurs par défaut ?')) {
      const resetValues = {
        defaultInterestRate: '',
        defaultAmortizationPeriod: '25',
        defaultDownPaymentPercent: '20',
        defaultRentGrowthRate: '2',
        defaultAppreciationRate: '3',
        defaultExpenseGrowthRate: '2',
        defaultVacancyRate: '5',
        defaultCollectionRate: '95',
        defaultManagementFeePercent: '8'
      };
      setDefaultValues(resetValues);
      localStorage.setItem('propertyDefaults', JSON.stringify(resetValues));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const handleChangePlan = async (newTier, billingPeriod = 'month') => {
    if (!subscription) {
      // If no subscription, redirect to pricing page
      navigate('/subscription-pricing');
      return;
    }

    const currentTier = stripeService.normalizeTier(userProfile?.subscription_tier);
    if (currentTier === newTier) {
      alert('Vous êtes déjà sur ce plan.');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir passer au plan ${getPlanConfig(newTier).name} ?`)) {
      return;
    }

    setChangingPlan(true);
    try {
      const { data, error } = await stripeService?.changeSubscriptionPlan(
        newTier,
        billingPeriod,
        hasFirstYearDiscount && billingPeriod === 'year'
      );

      if (error) {
        throw new Error(error);
      }

      // Refresh profile to get updated subscription tier
      if (refreshProfile) {
        await refreshProfile();
      }

      // Show appropriate message based on upgrade/downgrade
      const isUpgrade = data?.subscription?.isUpgrade;
      const isDowngrade = data?.subscription?.isDowngrade;
      
      if (isDowngrade) {
        const effectiveDate = data?.subscription?.effectiveDate 
          ? new Date(data.subscription.effectiveDate).toLocaleDateString('fr-CA')
          : 'la prochaine facturation';
        alert(`Votre plan sera modifié à ${effectiveDate}. Vous conserverez votre plan actuel jusqu'à cette date.`);
      } else if (isUpgrade) {
        alert('Votre plan a été mis à niveau avec succès ! Le changement est immédiat et vous avez été facturé au prorata.');
      } else {
        alert('Votre plan a été modifié avec succès !');
      }
      
      // Reload subscription data
      const { data: newSubData } = await stripeService?.getUserSubscription(userProfile.id);
      if (newSubData) {
        setSubscription(newSubData);
      }
    } catch (error) {
      console.error('Error changing plan:', error);
      alert(`Erreur lors du changement de plan: ${error?.message || 'Une erreur est survenue'}`);
    } finally {
      setChangingPlan(false);
    }
  };

  const getTierLabel = (tier) => {
    return stripeService.getTierDisplayName(tier);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-background)' }}>
      <NavigationHeader user={userProfile} subscriptionTier={userProfile?.subscription_tier || PLAN_SLUGS.TRIAL} />
      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: 'var(--spacing-lg) var(--spacing-md)',
        paddingTop: 'calc(64px + var(--spacing-lg))'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-md)',
          marginBottom: 'var(--spacing-lg)'
        }}>
          <Button
            variant="ghost"
            size="sm"
            iconName="ArrowLeft"
            onClick={() => navigate('/main-dashboard')}
          >
            Retour
          </Button>
          <h1 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '2rem',
            fontWeight: 700,
            color: 'var(--color-foreground)'
          }}>
            Paramètres
          </h1>
        </div>

        {saved && (
          <div style={{
            background: 'var(--color-success)',
            color: 'white',
            padding: 'var(--spacing-md)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--spacing-lg)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-sm)'
          }}>
            <Icon name="CheckCircle" size={20} />
            <span>Paramètres sauvegardés avec succès</span>
          </div>
        )}

        <div style={{
          background: 'var(--color-card)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--color-border)',
          padding: 'var(--spacing-lg)',
          marginBottom: 'var(--spacing-lg)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-sm)',
            marginBottom: 'var(--spacing-lg)'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'var(--color-primary)/10',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icon name="Settings" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.5rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                marginBottom: '4px'
              }}>
                Valeurs par défaut
              </h2>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                color: 'var(--color-muted-foreground)'
              }}>
                Définissez les valeurs par défaut pour vos nouvelles analyses
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            <div style={{
              padding: 'var(--spacing-md)',
              background: 'var(--color-muted)/30',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)'
            }}>
              <h3 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-md)'
              }}>
                Financement
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--spacing-md)' }}
              className="md:grid-cols-2"
              >
                <Input
                  label="Taux d'intérêt par défaut (%)"
                  type="number"
                  placeholder="Ex: 5.5"
                  value={defaultValues.defaultInterestRate}
                  onChange={(e) => handleChange('defaultInterestRate', e.target.value)}
                  min="0"
                  max="20"
                  step="0.1"
                />

                <Input
                  label="Période d'amortissement par défaut (ans)"
                  type="number"
                  placeholder="Ex: 25"
                  value={defaultValues.defaultAmortizationPeriod}
                  onChange={(e) => handleChange('defaultAmortizationPeriod', e.target.value)}
                  min="0"
                  max="50"
                  step="1"
                />

                <Input
                  label="Mise de fonds par défaut (%)"
                  type="number"
                  placeholder="Ex: 20"
                  value={defaultValues.defaultDownPaymentPercent}
                  onChange={(e) => handleChange('defaultDownPaymentPercent', e.target.value)}
                  min="0"
                  max="100"
                  step="1"
                />
              </div>
            </div>

            <div style={{
              padding: 'var(--spacing-md)',
              background: 'var(--color-muted)/30',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)'
            }}>
              <h3 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-md)'
              }}>
                Projections
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--spacing-md)' }}
              className="md:grid-cols-2"
              >
                <Input
                  label="Croissance des loyers par défaut (%)"
                  type="number"
                  placeholder="Ex: 2"
                  value={defaultValues.defaultRentGrowthRate}
                  onChange={(e) => handleChange('defaultRentGrowthRate', e.target.value)}
                  min="0"
                  max="20"
                  step="0.1"
                />

                <Input
                  label="Appréciation de la valeur par défaut (%)"
                  type="number"
                  placeholder="Ex: 3"
                  value={defaultValues.defaultAppreciationRate}
                  onChange={(e) => handleChange('defaultAppreciationRate', e.target.value)}
                  min="0"
                  max="20"
                  step="0.1"
                />

                <Input
                  label="Croissance des dépenses par défaut (%)"
                  type="number"
                  placeholder="Ex: 2"
                  value={defaultValues.defaultExpenseGrowthRate}
                  onChange={(e) => handleChange('defaultExpenseGrowthRate', e.target.value)}
                  min="0"
                  max="20"
                  step="0.1"
                />
              </div>
            </div>

            <div style={{
              padding: 'var(--spacing-md)',
              background: 'var(--color-muted)/30',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)'
            }}>
              <h3 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-md)'
              }}>
                Revenus et dépenses
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--spacing-md)' }}
              className="md:grid-cols-2"
              >
                <Input
                  label="Taux d'inoccupation par défaut (%)"
                  type="number"
                  placeholder="Ex: 5"
                  value={defaultValues.defaultVacancyRate}
                  onChange={(e) => handleChange('defaultVacancyRate', e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                />

                <Input
                  label="Taux de perception par défaut (%)"
                  type="number"
                  placeholder="Ex: 95"
                  value={defaultValues.defaultCollectionRate}
                  onChange={(e) => handleChange('defaultCollectionRate', e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                />

                <Input
                  label="Frais de gestion par défaut (%)"
                  type="number"
                  placeholder="Ex: 8"
                  value={defaultValues.defaultManagementFeePercent}
                  onChange={(e) => handleChange('defaultManagementFeePercent', e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: 'var(--spacing-md)',
              marginTop: 'var(--spacing-md)',
              paddingTop: 'var(--spacing-md)',
              borderTop: '1px solid var(--color-border)'
            }}>
              <Button
                variant="default"
                size="lg"
                iconName="Save"
                iconPosition="left"
                onClick={handleSave}
                disabled={loading}
                fullWidth
              >
                {loading ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={handleReset}
                fullWidth
              >
                Réinitialiser
              </Button>
            </div>
          </div>
        </div>

        {/* Subscription Management Section */}
        {subscription && (
          <div style={{
            background: 'var(--color-card)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border)',
            padding: 'var(--spacing-lg)',
            marginBottom: 'var(--spacing-lg)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-sm)',
              marginBottom: 'var(--spacing-lg)'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'var(--color-primary)/10',
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Icon name="CreditCard" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h2 style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: 'var(--color-foreground)',
                  marginBottom: '4px'
                }}>
                  Gestion de l'abonnement
                </h2>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--color-muted-foreground)'
                }}>
                  Changez de plan ou gérez votre abonnement
                </p>
              </div>
            </div>

            <div style={{
              padding: 'var(--spacing-md)',
              background: 'var(--color-muted)/30',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              marginBottom: 'var(--spacing-md)'
            }}>
              {(subscription?.current_period_start || subscription?.current_period_end) && (
                <div style={{
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  background: 'var(--color-muted)/20',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--spacing-md)'
                }}>
                  <p style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--color-muted-foreground)',
                    marginBottom: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Cycle de facturation
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    color: 'var(--color-foreground)',
                    margin: 0
                  }}>
                    {subscription.current_period_start && subscription.current_period_end
                      ? `Période en cours : du ${new Date(subscription.current_period_start).toLocaleDateString('fr-CA')} au ${new Date(subscription.current_period_end).toLocaleDateString('fr-CA')}`
                      : subscription.current_period_end
                        ? `Prochaine facturation : ${new Date(subscription.current_period_end).toLocaleDateString('fr-CA')}`
                        : null}
                  </p>
                </div>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--spacing-md)'
              }}>
                <div>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    color: 'var(--color-muted-foreground)',
                    marginBottom: '4px'
                  }}>
                    Plan actuel
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--color-foreground)'
                  }}>
                    {getTierLabel(userProfile?.subscription_tier || PLAN_SLUGS.TRIAL)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ExternalLink"
                  onClick={() => navigate('/subscription-pricing')}
                >
                  Voir tous les plans
                </Button>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 'var(--spacing-md)',
                marginTop: 'var(--spacing-md)'
              }}>
                {stripeService.normalizeTier(userProfile?.subscription_tier) !== PLAN_SLUGS.PRO && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => navigate('/subscription-pricing')}
                    disabled={changingPlan}
                    fullWidth
                    iconName="Crown"
                    data-testid="settings-upgrade-btn"
                  >
                    {changingPlan ? 'Changement...' : 'Voir les plans'}
                  </Button>
                )}
                {stripeService.normalizeTier(userProfile?.subscription_tier) === PLAN_SLUGS.PRO && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/subscription-pricing')}
                    fullWidth
                    iconName="Settings"
                    data-testid="settings-manage-btn"
                  >
                    Gerer mon abonnement
                  </Button>
                )}
              </div>

              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                color: 'var(--color-muted-foreground)',
                marginTop: 'var(--spacing-md)',
                fontStyle: 'italic'
              }}>
                Note: Les changements de plan sont facturés au prorata. Vous pouvez également gérer votre abonnement via le portail Stripe.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Settings;
